import { useState, useEffect, useCallback } from 'react';
export type Theme = 'light' | 'dark' | 'system';
export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(() => {
    const saved = localStorage.getItem('docforge-theme') as Theme;
    return saved || 'system';
  });
  const applyTheme = useCallback((targetTheme: Theme) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    let resolvedTheme = targetTheme;
    if (targetTheme === 'system') {
      resolvedTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    root.classList.add(resolvedTheme);
    localStorage.setItem('docforge-theme', targetTheme);
  }, []);
  // Sync state changes to DOM
  useEffect(() => {
    applyTheme(theme);
  }, [theme, applyTheme]);
  // Listen for system changes
  useEffect(() => {
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => applyTheme('system');
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme, applyTheme]);
  const toggleTheme = () => {
    setThemeState((current) => {
      if (current === 'light') return 'dark';
      if (current === 'dark') return 'system';
      return 'light';
    });
  };
  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };
  const isDark = theme === 'dark' || (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  return { theme, isDark, toggleTheme, setTheme };
}